import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { IcoActivity, IcoArrowRight } from '../common/Icons';
import { useStatusSummary, lv, HEADLINE, OWNER, ago, refreshSummary } from './statusMeta';

/**
 * A88 — the sidebar "System Status" row and the quick-look popup it opens.
 *
 * The row carries only a SMALL hint — a dot and one short word (user decision, v1.226.1; a big
 * coloured pill was too loud). Detail is shown only once someone clicks. Real outages still reach
 * everyone through StatusBanner.
 * The popup is portalled to <body>: the sidebar is `position: sticky`, which makes its own stacking
 * context, so a fixed popup inside it painted UNDER the page content whatever its z-index.
 */
// `dark` = the graphite sidebar skin (v1.238.0); the popup itself stays light.
export default function StatusWidget({ collapsed, dark }) {
  const s = useStatusSummary();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const overall = s ? s.overall : 'unknown';
  const L = lv(overall);
  // One short word, three states — a normal user should not have to decode five levels here.
  const hint = !s ? { word: '', color: '#cbd5e1' }
    : L.rank >= 2 ? { word: 'Issue', color: L.color }
    : L.rank === 1 ? { word: 'Slow', color: L.color }
    : { word: 'OK', color: '#16a34a' };

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ left: r.right + 10, bottom: Math.max(12, window.innerHeight - r.bottom) });
      refreshSummary();
    }
    setOpen(o => !o);
  };

  const issues = (s && s.issues) || [];
  const notices = (s && s.notices) || [];

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        title={hint.word ? `System Status: ${hint.word}` : 'System Status'}
        aria-expanded={open}
        style={dark
          ? { ...st.row, ...st.rowDark }
          : { ...st.row, justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '9px 0' : '9px 10px' }}
      >
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <IcoActivity size={17} style={{ verticalAlign: 0 }} />
          {collapsed && <span style={{ ...st.dotCorner, background: hint.color, ...(dark ? { borderColor: '#111114' } : null) }} />}
        </span>
        {!collapsed && <span style={st.label}>System Status</span>}
        {!collapsed && (
          <span style={{ ...st.hint, color: hint.color }}>
            <span style={{ ...st.dot, background: hint.color }} />{hint.word}
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div ref={popRef} role="dialog" aria-label="System status" style={{ ...st.pop, left: pos.left, bottom: pos.bottom }}>
          <div style={{ ...st.popHead, background: L.bg, borderBottom: `1px solid ${L.border}` }}>
            <L.Ico size={20} style={{ color: L.color, verticalAlign: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={st.popTitle}>{s ? HEADLINE[overall] : 'Checking status…'}</div>
              {s && <div style={st.popSub}>Checked {ago(s.checked_at)}</div>}
            </div>
          </div>

          <div style={st.popBody}>
            {notices.map(n => (
              <div key={n.id} style={{ ...st.item, borderLeft: `3px solid ${lv(n.level === 'info' ? 'maintenance' : n.level).color}` }}>
                <div style={st.itemTitle}>{n.title}</div>
                {n.message && <div style={st.itemMsg}>{n.message}</div>}
              </div>
            ))}
            {issues.length === 0 && notices.length === 0 && s && (
              <div style={st.allGood}>
                Everything is working — ModCommerce, your couriers and your sales channels. If something still looks wrong, open the full status page for details.
              </div>
            )}
            {issues.map(it => {
              const IL = lv(it.status);
              const O = it.owner && OWNER[it.owner];
              return (
                <div key={`${it.scope}-${it.key}`} style={st.item}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <IL.Ico size={14} style={{ color: IL.color }} />
                    <span style={st.itemTitle}>{it.name}</span>
                    {O && <span style={{ ...st.owner, color: O.color, background: O.bg }}>{O.label}</span>}
                  </div>
                  <div style={st.itemMsg}>{it.message}</div>
                </div>
              );
            })}
          </div>

          <button style={st.popCta} onClick={() => { setOpen(false); navigate('/dashboard/status'); }}>
            View full status page <IcoArrowRight size={14} />
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

const st = {
  row: {
    display: 'flex', alignItems: 'center', gap: 10, width: 'calc(100% - 16px)', margin: '4px 8px 6px',
    borderRadius: 8, border: '1px solid #eef2f7', background: '#fbfcfe', color: '#475569',
    cursor: 'pointer', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden',
    transition: 'background var(--mc-t-fast) var(--mc-ease)',
  },
  // Graphite: fixed left padding so the icon never jumps while the panel animates open/closed.
  rowDark: { width: 'calc(100% - 20px)', margin: '4px 10px 6px', padding: '9px 11px', justifyContent: 'flex-start', gap: 12, border: 'none', background: 'transparent', color: '#A1A1A6' },
  label: { flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' },
  hint: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, flexShrink: 0 },
  dot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  dotCorner: { position: 'absolute', top: -3, right: -4, width: 8, height: 8, borderRadius: '50%', border: '1.5px solid #fff' },
  pop: {
    position: 'fixed', zIndex: 1000, width: 360, maxWidth: 'calc(100vw - 24px)', maxHeight: '70vh',
    display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 12, boxShadow: 'var(--mc-e4)', overflow: 'hidden',
  },
  popHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px' },
  popTitle: { color: '#0f172a', fontSize: 15, fontWeight: 800 },
  popSub: { color: '#64748b', fontSize: 12, marginTop: 2 },
  popBody: { padding: '10px 12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  allGood: { color: '#475569', fontSize: 13, lineHeight: 1.5, padding: '4px 4px 6px' },
  item: { border: '1px solid #eef2f7', borderRadius: 8, padding: '9px 11px', background: '#fff' },
  itemTitle: { color: '#0f172a', fontSize: 13, fontWeight: 700 },
  itemMsg: { color: '#475569', fontSize: 12.5, lineHeight: 1.45, marginTop: 3 },
  owner: { fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '1px 7px', marginLeft: 'auto' },
  popCta: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 14px',
    border: 'none', borderTop: '1px solid #eef2f7', background: '#f8fafc', color: '#2563eb',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
};
